// @owners { team: patients-team }
/**
 * Imperative NAD fetch for cart/checkout: React Query `fetchQuery` + {@link fetchNextAvailableDatesV3}.
 * Callers supply explicit prescription ID lists and optional location/shipment context (no Redux in this module).
 */
import { type QueryClient } from '@tanstack/react-query';
import { nextAvailableDatesQueryKeys } from '~shared/features/next-available-date/queries/nextAvailableDatesQueryKeys';
import { staleTimes } from '~shared/queries/constants';
import {
  type FetchNextAvailableDatesV3Params,
  fetchNextAvailableDatesV3,
} from '~shared/queries/fetchNextAvailableDatesV3';

export type FetchNextAvailableDatesOptions = {
  unbundledPrescriptionIDs: number[];
  bundledPrescriptionIDs: number[];
  addressID: number | undefined;
  /** When `addressID` is absent, used as the v3 location fallback (first active prescription facility; may be null). */
  facilityID?: number | null;
  /** Edit-order checkout shipment context (`getShipmentID` may yield null). */
  shipmentID?: number | null;
};

export type ResolveCheckoutNextAvailableDatesResult =
  | { kind: 'no_location' }
  | { kind: 'empty_success' }
  | { kind: 'fetch'; params: FetchNextAvailableDatesV3Params };

/**
 * Resolves v3 NAD params from explicit checkout/cart inputs (no Redux reads).
 */
export const resolveCheckoutNextAvailableDatesParams = (
  options: FetchNextAvailableDatesOptions,
): ResolveCheckoutNextAvailableDatesResult => {
  const addressID = options.addressID;
  const facilityID = options.facilityID;
  const unbundled = options.unbundledPrescriptionIDs;
  const bundled = options.bundledPrescriptionIDs;
  const shipmentID = options.shipmentID;

  if (!addressID && (facilityID === undefined || facilityID === null)) {
    return { kind: 'no_location' };
  }

  if (unbundled.length === 0 && bundled.length === 0 && (shipmentID === undefined || shipmentID === null)) {
    return { kind: 'empty_success' };
  }

  const params: FetchNextAvailableDatesV3Params = {};

  if (typeof addressID === 'number') {
    params.addressID = addressID;
  } else if (typeof facilityID === 'number') {
    params.facilityID = facilityID;
  }

  params.unbundledPrescriptionIDs = unbundled;
  params.bundledPrescriptionIDs = bundled;

  if (typeof shipmentID === 'number') {
    params.shipmentID = shipmentID;
  }

  return { kind: 'fetch', params };
};

/** Query key for the resolved checkout NAD request (sorted ID lists, matches {@link nextAvailableDatesQueryKeys.forCheckout}). */
export const getCheckoutNextAvailableDatesQueryKey = (params: FetchNextAvailableDatesV3Params) =>
  nextAvailableDatesQueryKeys.forCheckout({
    addressID: params.addressID,
    facilityID: params.facilityID,
    unbundledPrescriptionIDs: params.unbundledPrescriptionIDs ?? [],
    bundledPrescriptionIDs: params.bundledPrescriptionIDs ?? [],
    shipmentID: params.shipmentID ?? null,
  });

export const fetchCheckoutNextAvailableDates = async (
  queryClient: QueryClient,
  options: FetchNextAvailableDatesOptions,
): Promise<boolean> => {
  const resolved = resolveCheckoutNextAvailableDatesParams(options);

  if (resolved.kind === 'no_location') {
    return false;
  }

  if (resolved.kind === 'empty_success') {
    return true;
  }

  const { params } = resolved;
  const queryKey = getCheckoutNextAvailableDatesQueryKey(params);
  const staleTimeMs = staleTimes.fiveMinutes;

  try {
    await queryClient.fetchQuery({
      queryKey,
      queryFn: () => fetchNextAvailableDatesV3(params),
      staleTime: staleTimeMs,
    });
    return true;
  } catch {
    return false;
  }
};
